import { clearConsoleLogs, clearNetworkLogs, getConsoleLogs, getDeviceInfo, getNetworkLogs } from '../telemetry';
import { type AttachmentFile, type QAFormData } from '../types';

export interface SubmitQAReportParams {
  apiKey: string;
  formData: QAFormData;
  autoScreenshot: Blob | null;
  extraFiles: AttachmentFile[];
}

export const deriveProjectId = (): string => {
  if (typeof window === 'undefined') return '2';
  const pathMatch = window.location.pathname.match(/(?:projects|scheduler|reports|project)\/([0-9]+)/i);
  if (pathMatch && pathMatch[1]) return pathMatch[1];

  const searchParams = new URLSearchParams(window.location.search);
  const searchProjectId = searchParams.get('projectId') || searchParams.get('project_id');
  if (searchProjectId) return searchProjectId;

  return '2'; // Default fallback project
};

export const buildDiagnosticMarkdown = (
  data: QAFormData,
  deviceInfo: ReturnType<typeof getDeviceInfo>,
  consoleLogs: ReturnType<typeof getConsoleLogs>,
  networkLogs: ReturnType<typeof getNetworkLogs>,
): string => {
  return [
    '### What Happened',
    data.description.trim(),
    '',
    data.actualResult.trim() ? `### Actual Result\n${data.actualResult.trim()}\n` : '',
    '---',
    '### 🛠 Environment & Diagnostics (Auto-captured)',
    `- **URL**: ${deviceInfo.url}`,
    `- **Area**: ${data.area}${data.area === 'Manager' ? ` > ${data.subArea}` : ''}`,
    `- **Reporter**: ${data.reporterName || 'Anonymous'} (${data.reporterEmail || 'No email'})`,
    `- **Viewport**: ${deviceInfo.viewport} | **Screen**: ${deviceInfo.screenResolution}`,
    `- **Browser**: ${deviceInfo.userAgent}`,
    `- **Timestamp**: ${deviceInfo.timestamp}`,
    '',
    `#### 🔴 Recent Console Errors / Warnings (${consoleLogs.length})`,
    consoleLogs.length > 0
      ? '```\n' +
        consoleLogs.map((l) => `[${l.timestamp.slice(11, 19)}] [${l.level.toUpperCase()}] ${l.message}`).join('\n') +
        '\n```'
      : '_No console errors recorded._',
    '',
    `#### 🌐 Recent GraphQL / Network Activity (${networkLogs.length})`,
    networkLogs.length > 0
      ? '```\n' +
        networkLogs
          .map(
            (n) =>
              `[${n.timestamp.slice(11, 19)}] ${n.type} ${n.name} => ${n.status} (${n.durationMs ?? 0}ms)${
                n.errors ? ` | Errors: ${n.errors.join(', ')}` : ''
              }`,
          )
          .join('\n') +
        '\n```'
      : '_No network activity recorded._',
  ]
    .filter(Boolean)
    .join('\n');
};

export const submitQAReport = async ({
  apiKey,
  formData: data,
  autoScreenshot,
  extraFiles,
}: SubmitQAReportParams): Promise<void> => {
  const deviceInfo = getDeviceInfo();
  const consoleLogs = getConsoleLogs();
  const networkLogs = getNetworkLogs();
  const projectId = deriveProjectId();
  const formattedDiagnostics = buildDiagnosticMarkdown(data, deviceInfo, consoleLogs, networkLogs);

  const payload = new FormData();
  // Standard backward-compatible fields
  payload.append('title', data.title.trim());
  payload.append('steps_to_reproduce', data.stepsToReproduce.trim() || 'Not specified');
  payload.append('expected_behavior', data.expectedResult.trim() || 'Expected normal behavior');
  payload.append('actual_behavior', formattedDiagnostics);
  payload.append('severity', data.severity);
  payload.append('project_id', projectId);

  // Screenshot attachment
  if (autoScreenshot) {
    payload.append('screenshot', autoScreenshot, 'screenshot.png');
  } else if (extraFiles.length > 0) {
    payload.append('screenshot', extraFiles[0].file, extraFiles[0].file.name);
  }

  // Discrete metadata fields
  payload.append('description', data.description.trim());
  payload.append('area', data.area);
  if (data.area === 'Manager') payload.append('sub_area', data.subArea);
  if (data.reporterName) payload.append('reporter_name', data.reporterName);
  if (data.reporterEmail) payload.append('reporter_email', data.reporterEmail);
  payload.append('page_url', deviceInfo.url);
  payload.append('device_info', JSON.stringify(deviceInfo));
  payload.append('console_errors', JSON.stringify(consoleLogs));
  payload.append('network_telemetry', JSON.stringify(networkLogs));

  // Additional file attachments
  extraFiles.forEach(({ file }, idx) => {
    payload.append(`attachment_${idx + 1}`, file, file.name);
  });

  const endpoint =
    (import.meta.env.VITE_QA_API_URL as string) || 'https://api.projects.ops.esseal.net/api/v1/qa_reports';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'X-API-Key': apiKey,
    },
    body: payload,
  });

  if (!response.ok) {
    throw new Error(`QA API responded with status ${response.status}: ${response.statusText}`);
  }

  clearConsoleLogs();
  clearNetworkLogs();
};
